import { ResponsiveTable } from "../mobile/ResponsiveTable";
/** 手机记录列表复用原有数据、勾选与业务操作；普通记录按字段卡片呈现，交互表头和对比矩阵保留横向浏览。 */
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { History, Undo2, Loader2 } from "lucide-react";

const fmt = (iso) => { if (!iso) return ""; const d = new Date(iso); return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; };

/* 公海导入记录 + 一键撤销（admin / CEO） */
export function PoolImportHistory({ api, zh, onChanged }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(null);
  const load = () => api.get("/crm/pool/imports").then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const undo = async (b) => {
    setBusy(b.id);
    try {
      const { data } = await api.post(`/crm/pool/imports/${b.id}/undo`);
      toast.success(zh ? `已撤销：删除 ${data.deleted} 个新增客户${data.kept ? `，${data.kept} 个已分配 / 已推进的保留` : ""}${data.reverted ? `，还原 ${data.reverted} 个补全字段` : ""}` : `Undone: ${data.deleted} deleted, ${data.kept} kept, ${data.reverted} reverted`);
      setConfirm(null); load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "撤销失败" : "Undo failed")); }
    setBusy("");
  };

  return (
    <>
      <Button variant="outline" size="sm" data-testid="pool-imports-btn" onClick={() => setOpen(true)}><History className="h-4 w-4 mr-1" />{zh ? "导入记录" : "Imports"}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="pool-imports-dialog">
          <DialogHeader>
            <DialogTitle>{zh ? "公海导入记录" : "Pool import history"}</DialogTitle>
            <DialogDescription>{zh ? "撤销只删除该批次仍在公海、未分配、未推进的新增客户；已分配 / 已推进的保留；补全过的空字段若未被改动则还原。" : "Undo removes still-unassigned new leads of the batch; assigned/progressed ones are kept."}</DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-auto border border-slate-200 rounded-lg">
            <ResponsiveTable className="w-full text-xs min-w-[640px] whitespace-nowrap">
              <thead className="bg-slate-50 text-slate-500 sticky top-0">
                <tr>{[zh ? "时间" : "Time", zh ? "文件" : "File", zh ? "新增" : "New", zh ? "补全" : "Filled", zh ? "重复 / 无效" : "Dup / Invalid", zh ? "操作人" : "By", zh ? "状态" : "Status", ""].map((h, i) => <th key={i} className="px-2 py-1.5 text-left">{h}</th>)}</tr>
              </thead>
              <tbody>
                {items.map((b) => (
                  <tr key={b.id} className="border-t border-slate-100" data-testid={`pool-import-batch-${b.id}`} data-undoable={b.undoable} data-undone={b.undoneAt ? "1" : "0"}>
                    <td className="px-2 py-1.5 text-slate-500">{fmt(b.at)}</td>
                    <td className="px-2 py-1.5 font-medium text-slate-800 max-w-[200px] truncate" title={b.filename}>{b.filename}</td>
                    <td className="px-2 py-1.5">{b.created}</td>
                    <td className="px-2 py-1.5">{b.filled}</td>
                    <td className="px-2 py-1.5 text-slate-500">{b.dup} / {b.invalid}</td>
                    <td className="px-2 py-1.5 text-slate-500">{b.byName || b.by}</td>
                    <td className="px-2 py-1.5">
                      {b.undoneAt ? <span className="rounded-full bg-slate-100 text-slate-500 px-1.5 py-0.5" data-testid={`pool-import-undone-${b.id}`}>{zh ? `已撤销 · 删 ${b.undoDeleted} 留 ${b.undoKept}` : `Undone · -${b.undoDeleted}`}</span>
                        : <span className="rounded-full bg-emerald-50 text-emerald-700 px-1.5 py-0.5">{zh ? `可撤销 ${b.undoable} / ${b.created}` : `${b.undoable}/${b.created} undoable`}</span>}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      {!b.undoneAt && (confirm === b.id ? (
                        <span className="inline-flex items-center gap-1">
                          <Button size="sm" variant="destructive" className="h-6 text-[11px]" data-testid={`pool-import-undo-confirm-${b.id}`} disabled={busy === b.id} onClick={() => undo(b)}>{busy === b.id ? <Loader2 className="h-3 w-3 animate-spin" /> : (zh ? `确认撤销 ${b.undoable}` : `Confirm ${b.undoable}`)}</Button>
                          <Button size="sm" variant="ghost" className="h-6 text-[11px]" data-testid={`pool-import-undo-cancel-${b.id}`} onClick={() => setConfirm(null)}>{zh ? "取消" : "Cancel"}</Button>
                        </span>
                      ) : (
                        <Button size="sm" variant="outline" className="h-6 text-[11px]" data-testid={`pool-import-undo-${b.id}`} onClick={() => setConfirm(b.id)}><Undo2 className="h-3 w-3 mr-1" />{zh ? "撤销" : "Undo"}</Button>
                      ))}
                    </td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={8} className="text-center text-slate-300 py-6" data-testid="pool-imports-empty">{zh ? "暂无导入记录" : "No imports yet"}</td></tr>}
              </tbody>
            </ResponsiveTable>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
